import Accordion from '@/common/components/Accordion'
import Select from '@/common/components/Select'
import useDebounce from '@/common/hooks/useDebounce'
import { Option } from '@/common/types/option'
import { BranchConsumptionRestriction } from '@/features/companyBranches/components/form/ConsumptionRestriction/BranchConsumptionRestriction'
import { ProductCategoryConsumptionRestriction } from '@/features/companyBranches/components/form/ConsumptionRestriction/ProductCategoryConsumptionRestriction'
import { useClientCompaniesAvailableToAllocateInBranchSummary } from '@/features/companyBranches/hooks/useClientCompaniesAvailableToAllocateInBranchSummary'
import { CompanyBranchValidationSchema } from '@/features/companyBranches/schemas/companyBranch.schema'
import { ClientCompanyAllowedCategoriesAndProductsTransformer } from '@/features/companyBranches/services/allowedCategoriesAndProductsTransformer'
import { CircleAlert } from 'lucide-react'
import { useFieldArray, useFormContext } from 'react-hook-form'

export const ConsumptionRestriction = ({ disabled }: { disabled?: boolean }) => {
  const [searchProductCategory, setSearchProductCategory] = useDebounce('')

  const { data: allowedProductCategories } = useClientCompaniesAvailableToAllocateInBranchSummary({
    filters: { search: searchProductCategory },
    mapper: ClientCompanyAllowedCategoriesAndProductsTransformer.optionsTransformer({
      key: 'categories',
      showAvailableTotal: false,
    }),
  })

  const { control } = useFormContext<CompanyBranchValidationSchema>()

  const {
    fields: productCategoryRestrictionsFields,
    append: appendProductCategoryRestrictions,
    remove: removeProductCategoryRestrictions,
  } = useFieldArray({
    control,
    name: 'product_category_restrictions_attributes',
  })

  const handleAppendProductCategory = (option: Option) => {
    if (option.value) {
      appendProductCategoryRestrictions({
        product_category_id: option.value,
        display_name: option.label,
      })
    }
  }

  return (
    <Accordion
      name="consumption_restriction"
      head={<p className="text-sm">Restrição de consumo</p>}
      body={
        <div className="flex flex-col gap-4">
          <BranchConsumptionRestriction disabled={disabled} />

          <p className="text-sm">Restrição de consumo por serviço</p>

          <div className="flex items-center gap-x-2">
            <CircleAlert strokeWidth={1} className="text-warning" />

            <div>
              <p className="text-xs">
                Ao definir restrições para serviços específicos, estas configurações terão prioridade sobre as
                restrições de consumo acima
              </p>
            </div>
          </div>

          <Select
            name="search_product_categories"
            placeholder="Pesquisar serviços"
            value=""
            options={
              allowedProductCategories?.filter((productCategory) => {
                return !productCategoryRestrictionsFields?.some(
                  ({ product_category_id }) => product_category_id === productCategory.value,
                )
              }) || []
            }
            onChange={(option) => handleAppendProductCategory(option as Option)}
            onInputChange={setSearchProductCategory}
            isDisabled={disabled}
          />

          {productCategoryRestrictionsFields.map((category, index) => (
            <ProductCategoryConsumptionRestriction
              key={category.id}
              category={category}
              categoryIndex={index}
              removeCategory={() => removeProductCategoryRestrictions(index)}
              disabled={disabled}
            />
          ))}
        </div>
      }
    />
  )
}
